<template>
  <div class="c-config-input-type u-block">
    <div class="c-config-input-info c-config-input-info-full">
      <label>{{ input.label }}</label>
      <p>{{ input.description }}</p>
    </div>

    <div class="c-landing-grid">
      <article
        v-for="(inputValue, index) in inputValues"
        :key="`${input.id}-${inputValue.pageName}`"
        class="c-landing-card"
      >
        <div class="c-landing-card__media">
          <img
            v-if="inputValue.imageUrl && !brokenImages[inputValue.pageName]"
            :src="inputValue.imageUrl"
            :alt="inputValue.pageName"
            loading="lazy"
            @error="markBroken(inputValue.pageName)"
          >
          <div
            v-else
            class="c-landing-card__placeholder"
          >
            <app-icon
              :name="inputValue.imageUrl ? 'broken_image' : 'image'"
              :size="28"
            />
            <span>{{ inputValue.imageUrl ? 'Image could not be loaded' : 'No image yet' }}</span>
          </div>
          <span class="c-landing-card__index">{{ index + 1 }}</span>
        </div>

        <header class="c-landing-card__header">
          <h4 class="c-landing-card__title">
            {{ inputValue.pageName }}
          </h4>
          <button
            type="button"
            class="c-landing-card__delete"
            :disabled="disabled"
            :title="`Remove ${inputValue.pageName}`"
            :aria-label="`Remove ${inputValue.pageName}`"
            @click="deletePage(index)"
          >
            <img src="@/assets/delete-button.svg" class="c-landing-items__delete-img" alt="">
          </button>
        </header>

        <div class="c-landing-card__fields">
          <label
            v-for="field in fields"
            :key="field.key"
            :class="['c-landing-card__field', { 'is-invalid': !inputValue[field.key] }]"
          >
            <span>{{ field.label }} <em aria-hidden="true">*</em></span>
            <input
              v-model="inputValue[field.key]"
              :class="['ui-field__input', { 'is-mono': field.mono }]"
              type="text"
              required
              :placeholder="field.placeholder"
              :disabled="disabled"
              @input="onFieldInput(inputValue, field.key)"
            >
            <small v-if="!inputValue[field.key]">Required</small>
          </label>
        </div>
      </article>

      <!-- add tile -->
      <div class="c-landing-add">
        <span class="c-landing-add__icon">
          <app-icon
            name="add"
            :size="24"
          />
        </span>
        <p class="c-landing-add__title">
          Add page
        </p>

        <template v-if="availableCustomPages.length">
          <select
            v-model="newPageSelect"
            class="ui-select"
            aria-label="Page to add"
            :disabled="disabled"
          >
            <option
              value=""
              disabled
            >
              Choose a page…
            </option>
            <option
              v-for="page in availableCustomPages"
              :key="page.id"
              :value="page.name"
            >
              {{ page.name }}
            </option>
          </select>
          <button
            type="button"
            class="ui-btn ui-btn--primary ui-btn--block"
            :disabled="addNewPageButtonDisabled"
            @click="addNewLandingPageItem()"
          >
            Add
          </button>
        </template>
        <p
          v-else
          class="c-landing-add__hint"
        >
          All pages are already added.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { get, snakeCase, isEmpty } from 'lodash'
import AppIcon from '../app-icon/AppIcon'

export default {
  name: 'LandingPageItemsFormField',

  components: {
    AppIcon,
  },

  props: {
    input: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    languages: {
      type: Array,
      required: true
    },
  },

  data() {
    return {
      inputValues: [],
      newPageSelect: '',
      brokenImages: {},
      fields: [
        { key: 'imageUrl', label: 'Image URL', placeholder: 'https://…' },
        { key: 'titleKey', label: 'Title key', placeholder: 'translation_key', mono: true },
        { key: 'descriptionKey', label: 'Description key', placeholder: 'translation_key_description', mono: true },
      ],
    }
  },

  computed: {
    ...mapGetters('customPages', ['customPages', 'customPagesFetched']),

    defaultInputValue() {
      try {
        return JSON.parse(get(this.input, 'defaultValue', []))
      } catch (e) {
        return []
      }
    },

    addNewPageButtonDisabled() {
      return isEmpty(this.newPageSelect) || this.disabled
    },

    availableCustomPages() {
      return this.customPages.filter(customPage => {
        return !this.inputValues?.some(inputValue => {
          return inputValue.pageName === customPage.name
        })
      })
    }
  },

  created() {
    this.inputValues = get(this.input, 'value', this.defaultInputValue) || []

    if (!this.customPagesFetched) {
      this.getCustomPages();
    }
  },

  methods: {
    ...mapActions('customPages', ['getCustomPages']),

    addNewLandingPageItem() {
      if (isEmpty(this.newPageSelect)) {
        return;
      }

      this.inputValues.push({
        pageName: this.newPageSelect,
        imageUrl: '',
        titleKey: snakeCase(this.newPageSelect),
        descriptionKey: snakeCase(this.newPageSelect) + '_description',
      })

      this.newPageSelect = '';
      this.setValue();
    },

    onFieldInput(inputValue, key) {
      // a new URL gets a fresh chance to load
      if (key === 'imageUrl') {
        this.$delete(this.brokenImages, inputValue.pageName);
      }

      this.setValue();
    },

    markBroken(pageName) {
      this.$set(this.brokenImages, pageName, true);
    },

    setValue() {
      this.$emit('update', {
        value: JSON.stringify(this.inputValues),
        isValid: this.isValid()
      })
    },

    isValid() {
      return this.inputValues.every(inputValue => !isEmpty(inputValue.imageUrl) && !isEmpty(inputValue.titleKey) && !isEmpty(inputValue.descriptionKey));
    },

    deletePage(index) {
      this.inputValues.splice(index, 1);
      this.$emit('update', {
        value: JSON.stringify(this.inputValues),
        isValid: this.isValid()
      })
    },
  },
}
</script>
<style lang="scss" scoped>
// same delete icon as every other table/list (src/assets/delete-button.svg)
.c-landing-items__delete-img {
  display: block;
  flex-shrink: 0;
  width: 20px;
  min-width: 20px;
  height: 20px;
}

.c-landing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  margin-top: 16px;
  font-family: var(--font-sans);
}

// ---------- item card ----------
.c-landing-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background-color: var(--bg-surface);
  box-shadow: var(--shadow-xs);
  overflow: hidden;
  transition: box-shadow var(--duration-fast) ease, border-color var(--duration-fast) ease;

  &:hover {
    border-color: var(--border-strong);
    box-shadow: var(--shadow-md);
  }
}

.c-landing-card__media {
  position: relative;
  aspect-ratio: 16 / 9;
  background-color: var(--bg-subtle);
  border-bottom: 1px solid var(--border-default);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.c-landing-card__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 100%;
  color: var(--text-muted);
  font-size: 12px;
}

.c-landing-card__index {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: var(--radius-full);
  background-color: rgba(10, 11, 13, 0.6);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

.c-landing-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 12px 4px 16px;
}

.c-landing-card__title {
  margin: 0;
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.c-landing-card__delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition: background-color var(--duration-fast) ease, color var(--duration-fast) ease;

  &:hover:not(:disabled) {
    background-color: var(--danger-bg);
    color: var(--danger-fg);
  }

  &:focus-visible {
    box-shadow: var(--focus-ring);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.c-landing-card__fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 16px 16px;
}

.c-landing-card__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;

  > span {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);

    em {
      font-style: normal;
      color: var(--danger-fg);
    }
  }

  .ui-field__input {
    height: 36px;
    padding: 0 10px;
    font-size: 13px;

    &.is-mono {
      font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
      font-size: 12.5px;
    }
  }

  small {
    font-size: 12px;
    color: var(--danger-fg);
  }

  &.is-invalid .ui-field__input {
    border-color: var(--danger-fg);
  }
}

// ---------- add tile ----------
.c-landing-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 240px;
  padding: 24px;
  border: 1.5px dashed var(--border-strong);
  border-radius: var(--radius-lg);
  background-color: var(--bg-subtle);
  text-align: center;

  .ui-select {
    max-width: 240px;
  }

  .ui-btn {
    max-width: 240px;
  }
}

.c-landing-add__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: var(--bg-surface);
  border: 1px solid var(--border-default);
  color: var(--brand-600);
}

.c-landing-add__title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.c-landing-add__hint {
  margin: 0;
  font-size: 13px;
  color: var(--text-muted);
}
</style>
